import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { createContext, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SETTINGS_SCOPE_QUERY_PARAM } from 'utils/settings';
import { getVisibleSettingsGroups } from './sectionRegistry';

export const SettingsPageContext = createContext(null);

const dirtyReducer = (state, action) => {
    if (state[action.id] === action.value) return state;
    return { ...state, [action.id]: action.value };
};

const SettingsPageProvider = ({ children }) => {
    const { auth, currentUser } = useAuth();
    const { course } = useApp();
    const { onBeforeNavigate } = useNavigationGuard();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation();
    const { search } = useLocation();
    const [dirtySections, dispatchDirty] = useReducer(dirtyReducer, {});
    const scope = useMemo(() => new URLSearchParams(search).get(SETTINGS_SCOPE_QUERY_PARAM), [search]);
    const groups = useMemo(
        () => getVisibleSettingsGroups({ role: auth?.role, course, currentUser, scope }),
        [auth?.role, course, currentUser, scope]
    );

    const setSectionDirty = useCallback((id, value) => {
        dispatchDirty({ id, value: Boolean(value) });
    }, []);

    const onBeforeNavigateHandler = useCallback(
        (proceed) => {
            if (!Object.values(dirtySections).some(Boolean)) return proceed();
            setDialog({
                open: true,
                message: t('dialog.leave-page.title', 'Leave Page?'),
                description: t('dialog.leave-page.description', 'If you leave this page, your changes will be lost.'),
                variant: 'destructive',
                actionButtonText: t('controls.leave', 'Leave'),
                cancelButtonText: t('controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    proceed();
                },
                onCancel: closeDialog,
                onClose: closeDialog,
            });
        },
        [closeDialog, dirtySections, setDialog, t]
    );

    useEffect(() => {
        onBeforeNavigate(onBeforeNavigateHandler);
        return () => onBeforeNavigate((proceed) => proceed());
    }, [onBeforeNavigate, onBeforeNavigateHandler]);

    const value = useMemo(() => ({ groups, setSectionDirty }), [groups, setSectionDirty]);

    return <SettingsPageContext.Provider value={value}>{children}</SettingsPageContext.Provider>;
};

export default SettingsPageProvider;
